import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { updatePreference } from './memoryApi';
import type { AppliedMemoryItem } from './streamGeneration';

/**
 * "✨ Personalised with N of your preferences" (§4.4) — shown on a draft that
 * actually used Doctor AI Memory, backed by the same `applied_memory` the
 * server also wrote to ai_call_log for this call. Each item has its own
 * "Don't use this" (archives the preference; it can be turned back on in
 * My AI preferences), and the whole panel opens from here too.
 */
export function PersonalisedChip({ applied, onOpenPreferences, onChanged }: {
  applied: AppliedMemoryItem[];
  onOpenPreferences?: () => void;
  /** Called after a "Don't use this" click, so the caller can refresh its own applied-memory count if it's showing one. */
  onChanged?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  if (!applied.length) return null;

  const dontUse = async (item: AppliedMemoryItem) => {
    setBusyId(item.id);
    try {
      await updatePreference(item.id, { active: false });
      toast({ variant: 'success', title: 'Turned off', description: `"${item.statement}" will no longer be applied.` });
      onChanged?.();
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not turn this off', description: e instanceof ApiError ? e.message : undefined });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button type="button" size="sm" variant="ghost" className="self-start text-teal">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          Personalised with {applied.length} of your preference{applied.length === 1 ? '' : 's'}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-[210] w-80 rounded-md border border-border bg-card p-3 shadow-lg">
          <div className="mb-2 text-xs font-semibold text-ink">Applied to this draft</div>
          <div className="flex flex-col gap-2">
            {applied.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-2 text-xs">
                <span className="flex-1">{item.statement}</span>
                <Button type="button" size="sm" variant="ghost" disabled={busyId === item.id} onClick={() => dontUse(item)}>
                  {busyId === item.id ? '…' : "Don't use this"}
                </Button>
              </div>
            ))}
          </div>
          {onOpenPreferences && (
            <Button type="button" size="sm" variant="ghost" className="mt-2 self-start" onClick={() => { setOpen(false); onOpenPreferences(); }}>
              Manage all preferences
            </Button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
